import {
  useQuery,
  useInfiniteQuery,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query'
import apiClient from '@/lib/api/client'
import { trackAddBookmark } from '@/lib/analytics'
import type { ApiResponse } from '@/lib/api/types'
import type { FacilitySummary, FacilityDetail, FacilitySearchFilter } from '@/types/facility'
import { useAuthStore } from '@/stores/authStore'
import { toast } from '@/components/ui/Toast'

/** 북마크 요청 */
export interface BookmarkRequest {
  facilityId: number
  memo?: string
}

/** 북마크 응답 */
export interface BookmarkResponse {
  id: number
  facilityId: number
  memo?: string
  createdAt: string
}

/**
 * 시설 상세 조회 훅.
 *
 * @param id - 시설 ID
 * @returns TanStack Query 결과
 */
export function useFacility(id: number) {
  return useQuery({
    queryKey: ['facility', id],
    queryFn: async () => {
      const response = await apiClient.get<ApiResponse<FacilityDetail>>(
        `/facilities/${id}`
      )
      // null 은 '시설 미존재 확정' 신호 — SSR 프리페치(entry-server.tsx)가 백엔드
      // 404 를 받으면 null 을 시드하고, FacilityDetailPage 가 not-found UI 를 렌더한다.
      return (response.data.data ?? null) as FacilityDetail | null
    },
    enabled: id > 0,
  })
}

/**
 * 시설 목록 무한 스크롤 조회 훅.
 *
 * @param filter - 검색 필터
 * @returns TanStack Query 무한 스크롤 결과
 */
/** Spring Page 응답 구조 */
interface SpringPageResponse<T> {
  success: boolean
  data: {
    content: T[]
    totalElements: number
    totalPages: number
    number: number
    size: number
    last: boolean
    first: boolean
    empty: boolean
  }
  meta?: {
    page: number
    size: number
    totalElements: number
    totalPages: number
  }
}

export function useFacilities(filter: FacilitySearchFilter) {
  return useInfiniteQuery({
    queryKey: ['facilities', filter],
    queryFn: async ({ pageParam = 0 }) => {
      // 검색 파라미터 구성 (배열은 반복 파라미터로 전송)
      const params = new URLSearchParams()
      if (filter.keyword) params.append('name', filter.keyword)
      if (filter.region) params.append('sido', filter.region)
      if (filter.sigungu) params.append('sigungu', filter.sigungu)
      if (filter.dong) params.append('dong', filter.dong)
      if (filter.type && filter.type.length > 0) {
        // facilityType은 단일값 (백엔드가 단일 enum)
        if (filter.type[0]) params.append('facilityType', filter.type[0])
      }
      // evaluationGrade는 복수 선택 가능 → 반복 파라미터
      if (filter.grade && filter.grade.length > 0) {
        filter.grade.forEach((g) => params.append('evaluationGrade', g))
      }
      if (filter.careGrade) params.append('careGrade', filter.careGrade)
      if (filter.sortBy) params.append('sortBy', filter.sortBy)
      if (filter.latitude) params.append('latitude', String(filter.latitude))
      if (filter.longitude) params.append('longitude', String(filter.longitude))
      if (filter.radiusKm) params.append('radiusKm', String(filter.radiusKm))
      if (filter.hasDementiaCare) params.append('hasDementiaCare', 'true')
      if (filter.hasVacancy) params.append('hasVacancy', 'true')
      if (filter.hasPrograms) params.append('hasPrograms', 'true')
      params.append('page', String(pageParam))
      params.append('size', '20')

      const response = await apiClient.get<SpringPageResponse<FacilitySummary>>(
        `/facilities?${params.toString()}`
      )
      return response.data
    },
    getNextPageParam: (lastPage) => {
      // 방어: 응답 형태 이상(에러 바디 등)으로 data 가 없으면 다음 페이지 없음으로 처리(크래시 방지).
      const pageData = lastPage?.data
      if (!pageData || pageData.last) {
        return undefined
      }
      return pageData.number + 1
    },
    initialPageParam: 0,
  })
}

/**
 * 같은 지역(시도+시군구, 동일 시설유형) 시설 목록 — 시설 상세의 "같은 지역 시설" 내부링크용.
 *
 * <p>SEO: 시설 상세 → 인근 시설 상세로 가는 크롤 경로를 만들어 "발견됨-색인안됨" 페이지의
 * 색인을 유도한다. SSR 프리페치(entry-server.tsx)와 queryKey ['nearby-facilities', facilityId]를 공유한다.
 * 최대 7건을 받아 현재 시설을 제외하고 표시한다.</p>
 */
export function useNearbyFacilities(
  facilityId: number,
  sido?: string | null,
  sigungu?: string | null,
  facilityType?: string | null,
) {
  return useQuery({
    queryKey: ['nearby-facilities', facilityId],
    queryFn: async () => {
      const params = new URLSearchParams()
      if (sido) params.set('sido', sido)
      if (sigungu) params.set('sigungu', sigungu)
      if (facilityType) params.set('facilityType', facilityType)
      params.set('page', '0')
      params.set('size', '7')
      params.set('sortBy', 'RATING')
      const res = await apiClient.get<SpringPageResponse<FacilitySummary>>(
        `/facilities?${params.toString()}`,
      )
      return res.data.data?.content ?? []
    },
    enabled: !!facilityId && !!sido && !!sigungu,
    staleTime: 5 * 60 * 1000,
  })
}

/**
 * 내 북마크 시설 ID 목록 조회 (경량).
 * 시설 카드에서 하트 상태를 표시하기 위해 사용합니다.
 */
export function useBookmarkIds() {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated)

  return useQuery({
    queryKey: ['bookmarkIds'],
    queryFn: async () => {
      const response = await apiClient.get<ApiResponse<number[]>>('/bookmarks/ids')
      return new Set(response.data.data)
    },
    enabled: isAuthenticated,
    staleTime: 30 * 1000, // 30초 캐시
  })
}

/** axios 등 에러 객체에서 HTTP 상태코드를 안전하게 추출 */
function statusOf(err: unknown): number | undefined {
  const e = err as { response?: { status?: number }; status?: number } | null
  return e?.response?.status ?? e?.status
}

/** 북마크 관련 캐시 무효화(성공/실패 후 서버 상태와 재동기화) */
function invalidateBookmarkQueries(queryClient: ReturnType<typeof useQueryClient>) {
  queryClient.invalidateQueries({ queryKey: ['bookmarks'] })
  queryClient.invalidateQueries({ queryKey: ['bookmarkIds'] })
  queryClient.invalidateQueries({ queryKey: ['facilities'] })
  queryClient.invalidateQueries({ queryKey: ['facility'] })
  queryClient.invalidateQueries({ queryKey: ['recommendation'] })
}

/**
 * 북마크 추가 뮤테이션.
 * 낙관적 업데이트: 탭 즉시 bookmarkIds 캐시를 갱신해 하트가 바로 채워지고,
 * 연속 탭이 stale 플래그로 잘못 라우팅(중복 ADD→409)되는 문제를 막는다.
 * 409(이미 존재)는 원하는 상태(찜됨)가 이미 이뤄진 것이므로 실패로 보지 않는다.
 */
export function useAddBookmark() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (request: BookmarkRequest) => {
      const response = await apiClient.post<ApiResponse<BookmarkResponse>>(
        '/bookmarks',
        request
      )
      return response.data.data
    },
    onMutate: async (request: BookmarkRequest) => {
      await queryClient.cancelQueries({ queryKey: ['bookmarkIds'] })
      const previous = queryClient.getQueryData<Set<number>>(['bookmarkIds'])
      if (previous) {
        const next = new Set(previous)
        next.add(request.facilityId)
        queryClient.setQueryData(['bookmarkIds'], next)
      }
      return { previous }
    },
    onSuccess: () => {
      toast.success('관심 시설에 추가했어요')
    },
    onError: (err, _request, context) => {
      if (statusOf(err) === 409) return // 이미 찜됨 = 원하는 상태 달성, 롤백/에러 없음
      if (context?.previous) queryClient.setQueryData(['bookmarkIds'], context.previous)
      toast.error('찜하기에 실패했어요. 다시 시도해주세요.')
    },
    onSettled: () => invalidateBookmarkQueries(queryClient),
  })
}

/**
 * 북마크 삭제 뮤테이션.
 * 낙관적 업데이트 + 멱등: 404(이미 없음)는 원하는 상태(해제)가 이뤄진 것이므로 실패로 보지 않는다.
 */
export function useRemoveBookmark() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (facilityId: number) => {
      await apiClient.delete(`/bookmarks/${facilityId}`)
    },
    onMutate: async (facilityId: number) => {
      await queryClient.cancelQueries({ queryKey: ['bookmarkIds'] })
      const previous = queryClient.getQueryData<Set<number>>(['bookmarkIds'])
      if (previous) {
        const next = new Set(previous)
        next.delete(facilityId)
        queryClient.setQueryData(['bookmarkIds'], next)
      }
      return { previous }
    },
    onSuccess: () => {
      toast.success('관심 시설에서 제거했어요')
    },
    onError: (err, _facilityId, context) => {
      if (statusOf(err) === 404) return // 이미 해제됨 = 원하는 상태 달성, 롤백/에러 없음
      if (context?.previous) queryClient.setQueryData(['bookmarkIds'], context.previous)
      toast.error('찜 해제에 실패했어요. 다시 시도해주세요.')
    },
    onSettled: () => invalidateBookmarkQueries(queryClient),
  })
}

/**
 * 북마크 토글 훅.
 * isBookmarked 상태에 따라 추가/삭제를 자동으로 처리합니다.
 */
export function useToggleBookmark() {
  const addBookmark = useAddBookmark()
  const removeBookmark = useRemoveBookmark()

  const toggle = (facilityId: number, isBookmarked: boolean) => {
    if (isBookmarked) {
      removeBookmark.mutate(facilityId)
    } else {
      addBookmark.mutate({ facilityId })
      trackAddBookmark(facilityId)
    }
  }

  return {
    toggle,
    isLoading: addBookmark.isPending || removeBookmark.isPending,
  }
}

// =========================================================================
// SEO 지역별 랜딩 페이지 API
// =========================================================================

export interface RegionDto {
  sido: string
  sigungus: string[]
}

export interface RegionStatsResponse {
  totalCount: number
  averageGrade: string
  gradeACount: number
  gradeBCount: number
  gradeCCount: number
}

/** 시도-시군구 지역 목록 조회 */
export function useRegions() {
  return useQuery<RegionDto[]>({
    queryKey: ['regions'],
    queryFn: async () => {
      const res = await apiClient.get('/facilities/regions')
      return res.data.data
    },
    staleTime: 24 * 60 * 60 * 1000,
  })
}

export interface DongItem {
  dong: string
  count: number
}

/**
 * 시군구의 동(洞) 목록 조회 — 시군구 랜딩에서 동 랜딩으로 내부링크 생성용.
 * 시설 수가 사이트맵 동 페이지 기준(기본 3) 이상인 동만 반환된다.
 */
export function useDongs(sido?: string, sigungu?: string) {
  return useQuery<DongItem[]>({
    queryKey: ['dongs', sido, sigungu],
    queryFn: async () => {
      const params = new URLSearchParams({ sido: sido!, sigungu: sigungu! })
      const res = await apiClient.get(`/facilities/dongs?${params}`)
      return res.data.data
    },
    enabled: !!sido && !!sigungu,
    staleTime: 24 * 60 * 60 * 1000,
  })
}

/** 지역별 시설 통계 조회 */
export function useRegionStats(
  sido: string,
  sigungu?: string,
  facilityType?: string,
  dong?: string,
) {
  return useQuery<RegionStatsResponse>({
    queryKey: ['regionStats', sido, sigungu, facilityType, dong],
    queryFn: async () => {
      const params = new URLSearchParams({ sido })
      if (sigungu) params.append('sigungu', sigungu)
      if (dong) params.append('dong', dong)
      if (facilityType) params.append('facilityType', facilityType)
      const res = await apiClient.get(`/facilities/region-stats?${params}`)
      return res.data.data
    },
    enabled: !!sido,
  })
}
